import React, { ReactNode, useMemo, useState } from 'react';
import { Stack, Button, Menu, MenuItem, IconButton } from '@mui/material';
import { KeyboardArrowDown, Login } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { HeaderAppBar } from './app-bar';
import { Auth, AuthProps } from '../../components/auth/auth';
import { useMuiSwitchableTheme } from '../../theme';

export interface SiteHeaderNavLink {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  dropDownItems?: {
    label: string;
    href?: string;
    onClick?: () => void;
    external?: boolean;
  }[];
}

interface SiteHeaderProps<TUser> {
  title: ReactNode;
  navLinks: SiteHeaderNavLink[];
  loginFn: () => Promise<void>;
  logoutFn: () => Promise<void>;
  isProduction?: boolean;
  env?: string;
  user?: TUser;
  logo: ReactNode;
  dropdownItems?: AuthProps<TUser>['dropdownItems'];
  vendorName: string;
  persistentLeftNav?: boolean;
}

const SignInButtons: React.FC<{ loginFn: () => Promise<void> }> = ({
  loginFn,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { navCollapsed, navMode, isTablet, themeMode } =
    useMuiSwitchableTheme();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const oneTimePasswordLogin = (connection: 'email' | 'sms') => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(connection === 'email' ? 'useEmail' : 'useSMS', 'true');
    navigate('/login?' + searchParams.toString(), {
      replace: true,
      unstable_flushSync: true, // force sync to update search params immediately
    });
  };

  const showIconOnly = useMemo(
    () => navMode === 'left' && navCollapsed && !isTablet,
    [navMode, navCollapsed, isTablet],
  );

  return (
    <Stack
      direction={navMode === 'top' && !isTablet ? 'row' : 'column'}
      spacing={2}
    >
      {!showIconOnly ? (
        <>
          <Button
            onClick={loginFn}
            variant="contained"
            sx={{ backgroundColor: 'primary.dark' }}
          >
            Log In
          </Button>
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDown />}
            onClick={handleMenuOpen}
            sx={{
              color: 'primary.contrastText',
              borderColor: 'primary.contrastText',
              filter:
                themeMode === 'light' ? 'brightness(0.9)' : 'brightness(1.1)',
            }}
          >
            Customer Log In
          </Button>
        </>
      ) : (
        <IconButton
          onClick={handleMenuOpen}
          sx={{ color: 'primary.contrastText' }}
        >
          <Login />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              my: 1,
            },
          },
        }}
      >
        {showIconOnly && (
          <MenuItem
            onClick={() => {
              loginFn();
              handleMenuClose();
            }}
          >
            Processor Login
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            oneTimePasswordLogin('email');
            handleMenuClose();
          }}
        >
          Login Using Email
        </MenuItem>
        <MenuItem
          onClick={() => {
            oneTimePasswordLogin('sms');
            handleMenuClose();
          }}
        >
          Login Using Phone
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export function SiteHeader<
  TUser extends { first_name?: string; picture_url?: string | null },
>({
  navLinks,
  loginFn,
  logoutFn,
  user,
  dropdownItems = [],
  vendorName,
  env = '',
}: SiteHeaderProps<TUser>) {
  return (
    <HeaderAppBar
      links={navLinks}
      baseColor="primary"
      env={env}
      authComponent={
        user ? (
          <Auth<TUser>
            user={user}
            logoutFn={logoutFn}
            dropdownItems={dropdownItems}
            vendorName={vendorName}
          />
        ) : (
          <SignInButtons loginFn={loginFn} />
        )
      }
    />
  );
}
